import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loginScreen } from '../../services/screenApi';
import { 
  clearScreenAuthData, 
  persistScreenAuthData, 
  persistScreenUsername, 
  getScreenUsername, 
  clearScreenUsername 
} from '../../utils/screenAuthStorage';
import useScreenAuth from '../../components/useScreenAuth';

const ScreenLogin = () => {
  const [screenCode, setScreenCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUsernameInput, setShowUsernameInput] = useState(false);
  const [username, setUsername] = useState('');
  const [loginResponse, setLoginResponse] = useState(null);
  const [editingUsername, setEditingUsername] = useState(false);
  const [savedUsername, setSavedUsername] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, views } = useScreenAuth();

  // Disable caching and load saved username on component mount
  useEffect(() => {
    // Force no-cache headers via meta tags
    const addMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[http-equiv="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('http-equiv', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    addMetaTag('Cache-Control', 'no-cache, no-store, must-revalidate');
    addMetaTag('Pragma', 'no-cache');
    addMetaTag('Expires', '0');

    // Load saved username with fallback
    const storedUsername = getScreenUsername();
    if (storedUsername) {
      setSavedUsername(storedUsername);
      setUsername(storedUsername);
    }

    // Cleanup meta tags on unmount
    return () => {
      const metaTags = ['Cache-Control', 'Pragma', 'Expires'];
      metaTags.forEach(name => {
        const meta = document.querySelector(`meta[http-equiv="${name}"]`);
        if (meta) {
          meta.remove();
        }
      });
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!screenCode.trim()) {
      toast.error('Veuillez saisir le code écran');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await loginScreen(screenCode.trim());
      const normalized = {
        token: response?.token || response?.token_jwt,
        screen_code: response?.screen_code || screenCode.trim(),
        store_ids: response?.store_ids || [],
        order_status: response?.order_status || [],
        views: response?.views || [],
        expiry_date: response?.expiry_date,
      };
      console.log(normalized);
      
      if (!normalized.token) {
        toast.error('Impossible de récupérer le jeton de l\'écran');
        return;
      }

      // Check if user view is included - if so, ask for username first
      if (normalized.views?.includes('user')) {
        setLoginResponse(normalized);
        setShowUsernameInput(true);
        toast.success('Connexion écran réussie - Veuillez saisir votre nom d\'utilisateur');
        return;
      }

      // No user view, proceed with normal login
      persistScreenAuthData(normalized);
      toast.success('Connexion écran réussie');
      
      // Determine redirect path based on views
      let redirectPath = location.state?.from?.pathname;
      
      if (!redirectPath) {
        // Auto-redirect based on views
        if (normalized.views?.includes('orders')) {
          redirectPath = '/screens/orders';
        } else if (normalized.views?.includes('report')) {
          redirectPath = '/screens/reports';
        } else {
          redirectPath = '/screens/orders'; // fallback
        }
      }
      
      navigate(redirectPath);
    } catch (error) {
      clearScreenAuthData();
      if (error?.response?.status === 401) {
        toast.error('Code écran invalide ou expiré');
      } else {
        toast.error('Erreur lors de la connexion');
      }
      console.error('Screen login failed', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUsernameSubmit = (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error('Veuillez saisir votre nom d\'utilisateur');
      return;
    }

    if (!loginResponse) {
      toast.error('Erreur: données de connexion manquantes');
      return;
    }

    // Store username in session with force storage
    const trimmedUsername = username.trim();
    persistScreenUsername(trimmedUsername);
    setSavedUsername(trimmedUsername);
    
    // Complete login process
    persistScreenAuthData(loginResponse);
    toast.success('Connexion réussie');
    
    // Navigate to user orders view
    navigate('/screens/user-orders');
  };

  const handleEditUsername = () => {
    setEditingUsername(true);
  };

  const handleSaveUsername = () => {
    if (!username.trim()) {
      toast.error('Veuillez saisir votre nom d\'utilisateur');
      return;
    }
    
    const trimmedUsername = username.trim();
    persistScreenUsername(trimmedUsername);
    setSavedUsername(trimmedUsername);
    setEditingUsername(false);
    toast.success('Nom d\'utilisateur mis à jour');
  };

  const handleCancelEditUsername = () => {
    setUsername(savedUsername);
    setEditingUsername(false);
  };

  const handleLogout = () => {
    clearScreenUsername(); // Clear stored username
    setSavedUsername('');
    setUsername('');
    clearScreenAuthData();
    toast.info('Session écran fermée');
  };

  return (
    <div className="container py-5" style={{ maxWidth: '480px' }}>
      <h2 className="mb-4 text-center">Connexion écran</h2>
      
      {showUsernameInput ? (
        // Username input step
        <form onSubmit={handleUsernameSubmit} className="card p-4 shadow-sm">
          <div className="alert alert-info mb-3">
            <i className="fas fa-info-circle me-2"></i>
            Connexion réussie. Veuillez saisir votre nom d'utilisateur pour continuer.
          </div>
          <div className="mb-3">
            <label htmlFor="username" className="form-label">Nom d'utilisateur</label>
            <input
              id="username"
              className="form-control form-control-lg"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Votre nom d'utilisateur"
              autoFocus
            />
          </div>
          <button type="submit" className="btn btn-success btn-lg w-100">
            <i className="fas fa-arrow-right me-2"></i>
            Continuer
          </button>
          <button
            type="button"
            className="btn btn-link mt-3"
            onClick={() => {
              setShowUsernameInput(false);
              setLoginResponse(null);
              setUsername('');
            }}
          >
            Retour
          </button>
        </form>
      ) : (
        // Screen code input step
        <>
          {isAuthenticated && (
            <div className="mb-4">
              <div className="alert alert-success" role="alert">
                Un écran est déjà connecté. Vous pouvez le remplacer en soumettant un nouveau code.
              </div>
              {savedUsername && views && views.includes('user') && (
                <div className="card p-3 mb-3 bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-muted">Utilisateur connecté</small>
                      {!editingUsername ? (
                        <h5 className="mb-0">
                          <i className="fas fa-user me-2"></i>
                          {savedUsername}
                        </h5>
                      ) : (
                        <div className="mt-2">
                          <input
                            className="form-control"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Nom d'utilisateur"
                            autoFocus
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      {!editingUsername ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleEditUsername}
                        >
                          <i className="fas fa-edit me-1"></i>
                          Modifier
                        </button>
                      ) : (
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={handleSaveUsername}
                          >
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={handleCancelEditUsername}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {views && views.length > 0 && (
                <div className="d-grid gap-2">
                  {views.includes('user') && views.includes('orders') && (
                    <button 
                      type="button" 
                      className="btn btn-success btn-lg"
                      onClick={() => navigate('/screens/user-orders')}
                    >
                      <i className="fas fa-user-check me-2"></i>
                      Mes commandes
                    </button>
                  )}
                  {views.includes('orders') && (
                    <button 
                      type="button" 
                      className="btn btn-primary btn-lg"
                      onClick={() => navigate('/screens/orders')}
                    >
                      <i className="fas fa-shopping-cart me-2"></i>
                      Accéder aux commandes
                    </button>
                  )}
                  {views.includes('report') && (
                    <button 
                      type="button" 
                      className="btn btn-info btn-lg"
                      onClick={() => navigate('/screens/reports')}
                    >
                      <i className="fas fa-chart-bar me-2"></i>
                      Accéder aux rapports
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit} className="card p-4 shadow-sm">
            <div className="mb-3">
              <label htmlFor="screenCode" className="form-label">Code écran</label>
              <input
                id="screenCode"
                className="form-control form-control-lg"
                type="text"
                value={screenCode}
                onChange={(e) => setScreenCode(e.target.value)}
                placeholder="XXXX-XXX-XXX"
                disabled={isSubmitting}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-lg w-100" disabled={isSubmitting}>
              {isSubmitting ? 'Connexion en cours...' : 'Se connecter'}
            </button>
            <button
              type="button"
              className="btn btn-link mt-3"
              onClick={handleLogout}
              disabled={isSubmitting}
            >
              Se déconnecter
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default ScreenLogin;
